const JPEG = new Set(["image/jpeg", "image/jpg", "image/pjpeg"]);

let sequence = 0;
const zones = new Set();
let focusedId = null;

export function createFileDropZoneId() {
    sequence += 1;
    return sequence;
}

export function resetFileDropZones() {
    sequence = 0;
    zones.clear();
    focusedId = null;
}

export function mountFileDropZone(id) {
    zones.add(id);
}

export function unmountFileDropZone(id) {
    zones.delete(id);
    if (focusedId === id) focusedId = null;
}

export function focusFileDropZone(id) {
    focusedId = id;
}

export function blurFileDropZone(id) {
    if (focusedId === id) focusedId = null;
}

export function shouldArmForWindowDrag(id) {
    return zones.size === 1 && zones.has(id);
}

export function shouldTakeDocumentPaste(id, event) {
    if (!zones.has(id)) return false;
    if (!clipboardFiles(event?.clipboardData).length) return false;
    if (isTextEditingTarget(event?.target)) return false;
    if (focusedId === id) return true;
    return focusedId == null && zones.size === 1;
}

export function acceptTokens(accept) {
    return String(accept || "")
        .split(",")
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean);
}

export function fileMatchesAccept(file, accept) {
    const tokens = acceptTokens(accept);
    if (!tokens.length) return true;
    const name = String(file?.name || "").toLowerCase();
    const type = String(file?.type || "").toLowerCase();
    return tokens.some((token) => {
        if (token.startsWith(".")) return name.endsWith(token);
        if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1));
        if (JPEG.has(token) && JPEG.has(type)) return true;
        return type === token;
    });
}

export function selectFiles(files, { accept = "", multiple = false } = {}) {
    const list = Array.from(files || []).filter(Boolean);
    const accepted = list.filter((file) => fileMatchesAccept(file, accept));
    return {
        files: multiple ? accepted : accepted.slice(0, 1),
        rejected: list.length - accepted.length,
    };
}

export function rejectionMessage(rejected, chosenCount) {
    if (!rejected) return "";
    if (!chosenCount) return "That file type isn't accepted here.";
    return "Some files were skipped because of their type.";
}

const IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"]);

export function dropHint(accept) {
    const tokens = acceptTokens(accept);
    const imagesOnly = tokens.length > 0 && tokens.every((token) =>
        token.startsWith("image/") || IMAGE_EXTENSIONS.has(token));
    return imagesOnly
        ? "Drop an image here, paste one, or click to browse"
        : "Drop a file here, paste one, or click to browse";
}

export function isPreviewable(file) {
    if (!file) return false;
    const type = String(file.type || "").toLowerCase();
    const name = String(file.name || "").toLowerCase();
    return type.startsWith("image/") || name.endsWith(".svg");
}

export function fileSummary(files) {
    if (!files?.length) return "";
    if (files.length === 1) return files[0].name || "Pasted image";
    return `${files.length} files`;
}

export function filesFromList(fileList) {
    return Array.from(fileList || []).filter(Boolean);
}

export function clipboardFiles(clipboardData) {
    const found = [];
    const push = (file) => {
        if (!file) return;
        const key = `${file.name}|${file.size}|${file.type}|${file.lastModified}`;
        if (found.some((entry) => entry.key === key)) return;
        found.push({ key, file });
    };
    if (clipboardData?.files) {
        for (const file of clipboardData.files) push(file);
    }
    if (clipboardData?.items) {
        for (const item of clipboardData.items) {
            if (item?.kind === "file" && typeof item.getAsFile === "function") push(item.getAsFile());
        }
    }
    return found.map((entry) => entry.file);
}

export function dragHasFiles(dataTransfer) {
    const types = dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes("Files");
}

export function isTextEditingTarget(target) {
    if (!target || typeof target.tagName !== "string") return false;
    const tag = target.tagName.toLowerCase();
    if (tag === "textarea") return true;
    if (target.isContentEditable) return true;
    if (tag !== "input") return false;
    const type = String(target.type || "text").toLowerCase();
    return !["button", "checkbox", "color", "file", "hidden", "radio", "range", "reset", "submit"].includes(type);
}

export function extensionForMime(type) {
    const mime = String(type || "").toLowerCase();
    if (mime === "image/jpeg" || mime === "image/jpg" || mime === "image/pjpeg") return "jpg";
    if (mime === "image/svg+xml") return "svg";
    if (mime === "image/png") return "png";
    if (mime === "image/gif") return "gif";
    if (mime === "image/webp") return "webp";
    return "png";
}

export function applyFiles(input, files) {
    if (!input) return;
    const transfer = new DataTransfer();
    for (const file of files || []) transfer.items.add(file);
    input.files = transfer.files;
}
